import { readRawPref, STORAGE_PREFIX, writeRawPref } from '@/app/lib/paneKeys'
import type { QuantMarketState } from '@/app/hooks/useQuantMarket'
import type { QuantPreviewState } from '@/app/hooks/useQuantPreview'
import type { PreviewSupport } from '@/app/lib/quant/types'

/** Per-strategy preview timeframe and bar count (spec 039 section 6). */
export const QUANT_PREVIEW_VIEW_KEY = `${STORAGE_PREFIX}quant-preview-view`

export interface PreviewView {
  tf: string
  bars: number
}

function readMap(): Record<string, unknown> {
  const raw = readRawPref(QUANT_PREVIEW_VIEW_KEY)
  if (raw === null) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) return parsed as Record<string, unknown>
  } catch {
    // corrupt JSON falls through to an empty map
  }
  return {}
}

function asView(value: unknown): PreviewView | null {
  if (typeof value !== 'object' || value === null) return null
  const v = value as Record<string, unknown>
  if (typeof v.tf !== 'string' || typeof v.bars !== 'number' || !Number.isInteger(v.bars) || v.bars < 1) return null
  return { tf: v.tf, bars: v.bars }
}

/** The stored view if it is still valid for this preview support; `null` otherwise. Never writes. */
export function readPreviewView(slug: string, preview: PreviewSupport): PreviewView | null {
  const view = asView(readMap()[slug])
  if (view === null) return null
  if (!preview.timeframes.includes(view.tf)) return null
  const cap = preview.max_bars[view.tf]
  if (typeof cap !== 'number' || view.bars > cap) return null
  return view
}

/** Remember a strategy's view, keeping every other strategy's valid entry. */
export function writePreviewView(slug: string, view: PreviewView): void {
  const next: Record<string, PreviewView> = {}
  for (const [key, value] of Object.entries(readMap())) {
    const v = asView(value)
    if (v !== null) next[key] = v
  }
  next[slug] = { tf: view.tf, bars: view.bars }
  writeRawPref(QUANT_PREVIEW_VIEW_KEY, JSON.stringify(next))
}

/** Spec 070: how the strategy page gets its preview. */
export type PreviewMode =
  | { kind: 'live' }
  /** `label` is the "no live preview" text (or the cap sentence), `null` while the feed answer is still pending. */
  | { kind: 'rest'; label: string | null }

export const NO_LIVE_PREVIEW = 'no live preview'

/**
 * `live` when the feed lists the instrument (or did not say, `liveInstruments === null`: try, and fall back on a
 * refusal); the still REST preview, labelled, when it is not listed or the feed cannot be read; no label while it loads.
 */
export function previewModeFor(args: {
  feed: 'loading' | 'unavailable' | 'loaded'
  liveInstruments: readonly string[] | null
  instrument: string
  fallbackCode: string | null
}): PreviewMode {
  if (args.fallbackCode !== null) {
    return { kind: 'rest', label: args.fallbackCode === 'view_cap_reached' ? `Two live views are already open · ${NO_LIVE_PREVIEW}` : NO_LIVE_PREVIEW }
  }
  if (args.feed === 'loading') return { kind: 'rest', label: null }
  if (args.feed === 'unavailable') return { kind: 'rest', label: NO_LIVE_PREVIEW }
  if (args.liveInstruments === null || args.liveInstruments.includes(args.instrument)) return { kind: 'live' }
  return { kind: 'rest', label: NO_LIVE_PREVIEW }
}

/** The live view's frames shaped as the REST preview's state, so one panel renders both. */
export function liveToPreviewState(m: QuantMarketState): QuantPreviewState {
  const strategy = m.indicators.strategy
  const failed = m.status === 'failed'
  const empty = m.candles.length === 0
  return {
    status: failed ? 'failed' : empty ? 'loading' : 'ready',
    candles: m.candles,
    total: m.candles.length,
    overlay: strategy === undefined ? null : { legend: [], stats: [], ...strategy.overlay, rev: strategy.rev },
    overlaySeq: strategy?.rev ?? 0,
    candleFrames: m.chunks,
    meta: null,
    error: failed && m.error !== null ? { message: m.error, code: m.errorCode, status: 0 } : null,
    pendingSeq: 0,
  }
}
